import Alpine from "alpinejs"
import { Splide } from "@splidejs/splide"
import { gsap } from "gsap"
import { MotionPathPlugin as motionPathPlugin } from "gsap/MotionPathPlugin"

gsap.registerPlugin(motionPathPlugin)

// Breakpoint for mobile/desktop switch
const MOBILE_BREAKPOINT = 1056

Alpine.data("teaserWheel", () => ({
    selectedTab: 1,
    wheels: [],
    initialized: false,
    sliders: {},
    isMobile: window.innerWidth < MOBILE_BREAKPOINT,
    resizeTimeout: null,
    splideOptions: {
        type: "loop",
        drag: true,
        mediaQuery: "min",
        autoWidth: true,
        focus: "center",
        gap: "1rem",
        breakpoints: {
            [MOBILE_BREAKPOINT]: {
                destroy: true,
            },
        },
        padding: "30%",
        arrows: false,
        isNavigation: false,
        pagination: false,
    },

    init() {
        // Add wheel event listener for carousel scrolling
        const wheelContainer = document.querySelector(".c-teaser-wheel__wheelpositioning")
        if (wheelContainer) {
            let isScrolling = false
            wheelContainer.addEventListener(
                "wheel",
                (e) => {
                    e.preventDefault()

                    if (isScrolling) return

                    isScrolling = true
                    setTimeout(() => {
                        isScrolling = false
                    }, 250)

                    if (e.deltaY > 0) {
                        this.down()
                    } else {
                        this.up()
                    }
                },
                { passive: false },
            )
        }

        // Add resize handler
        window.addEventListener("resize", () => {
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout)
            }

            this.resizeTimeout = setTimeout(() => {
                const wasIsMobile = this.isMobile
                this.isMobile = window.innerWidth < MOBILE_BREAKPOINT

                // Only reinitialize if we crossed the breakpoint
                if (wasIsMobile !== this.isMobile) {
                    this.reinitializeLayout()
                }
                this.updateMarkerSize()
            }, 250)
        })

        this.$nextTick(() => {
            this.initializeComponents()
            this.updateMarkerSize()
        })
    },

    initializeComponents() {
        // Initialize wheel data
        this.wheels[1] = {
            items: gsap.utils.toArray(".item-set1"),
            wrapper: null,
            numItems: 0,
            itemStep: 0,
            wrapProgress: null,
            snap: null,
            wrapTracker: null,
            tl: null,
            tracker: { item: 0 },
        }
        this.wheels[2] = {
            items: gsap.utils.toArray(".item-set2"),
            wrapper: null,
            numItems: 0,
            itemStep: 0,
            wrapProgress: null,
            snap: null,
            wrapTracker: null,
            tl: null,
            tracker: { item: 0 },
        }

        if (this.isMobile) {
            this.initializeSliders()
        } else {
            this.initializeWheels()
        }

        this.selectTab(1)
        this.initialized = true
    },

    reinitializeLayout() {
        // Cleanup existing components
        this.cleanup()

        // Reset states
        this.wheels = []
        this.sliders = {}

        // Reinitialize components
        this.initializeComponents()
    },

    cleanup() {
        // Cleanup sliders
        Object.values(this.sliders).forEach((slider) => {
            if (slider && typeof slider.destroy === "function") {
                slider.destroy()
            }
        })

        // Cleanup GSAP timelines
        Object.values(this.wheels).forEach((wheel) => {
            if (wheel && wheel.tl) {
                wheel.tl.kill()
            }
        })
    },

    initializeSliders() {
        try {
            const initSlider = (index) => {
                const slider = new Splide(this.$refs[`slider${index}`], this.splideOptions)

                // Add move event handler to update classes
                slider.on("moved", () => {
                    const slides = slider.Components.Elements.slides

                    // Remove all state classes first
                    slides.forEach((slide) => {
                        slide.classList.remove("is-prev", "is-next", "is-active", "aside-active")
                    })

                    // Get the current slide index
                    const currentIndex = slider.index
                    const totalSlides = slides.length

                    // Calculate prev and next indices considering loop
                    const prevIndex = (currentIndex - 1 + totalSlides) % totalSlides
                    const nextIndex = (currentIndex + 1) % totalSlides

                    // Add active class to current slide
                    slides[currentIndex].classList.add("is-active")

                    // Add aside-active only to immediate prev and next slides
                    slides[prevIndex].classList.add("aside-active")
                    slides[nextIndex].classList.add("aside-active")
                })

                slider.mount()
                return slider
            }

            this.sliders[1] = initSlider(1)
            this.sliders[2] = initSlider(2)

            // Initialize states for both sliders
            this.sliders[1].emit("moved")
            this.sliders[2].emit("moved")
        } catch (error) {
            console.error("Error initializing sliders:", error)
        }
    },

    initializeWheels() {
        ;[1, 2].forEach((tabIndex) => {
            if (this.wheels[tabIndex] && this.wheels[tabIndex].items.length) {
                this.setupItems(tabIndex)
            }
        })
    },

    setupItems(selectedTab) {
        let items = this.wheels[selectedTab].items
        if (!items.length || !items[0]) return

        this.wheels[selectedTab].wrapper = items[0].parentElement
        this.wheels[selectedTab].numItems = items.length
        this.wheels[selectedTab].itemStep = 1 / this.wheels[selectedTab].numItems
        this.wheels[selectedTab].wrapProgress = gsap.utils.wrap(0, 1)
        this.wheels[selectedTab].snap = gsap.utils.snap(this.wheels[selectedTab].itemStep)
        this.wheels[selectedTab].wrapTracker = gsap.utils.wrap(0, this.wheels[selectedTab].numItems)

        gsap.set(items, {
            motionPath: {
                path: "#circle",
                align: "#circle",
                alignOrigin: [0.5, 0.5],
                end: (i) => i / items.length,
            },
        })

        this.wheels[selectedTab].tl = gsap.timeline({ paused: true, reversed: true })
        this.wheels[selectedTab].tl.to(this.wheels[selectedTab].wrapper, {
            rotation: 360,
            transformOrigin: "center",
            duration: 1,
            ease: "none",
        })
        this.wheels[selectedTab].tl.to(
            items,
            {
                rotation: "-=360",
                transformOrigin: "center",
                duration: 1,
                ease: "none",
            },
            0,
        )
        this.wheels[selectedTab].tl.to(
            this.wheels[selectedTab].tracker,
            {
                item: this.wheels[selectedTab].numItems,
                duration: 1,
                ease: "none",
                modifiers: {
                    item: (value) =>
                        this.wheels[selectedTab].wrapTracker(this.wheels[selectedTab].numItems - Math.round(value)),
                },
            },
            0,
        )

        items.forEach((el, i) => {
            el.addEventListener("click", (e) => {
                this.handleItemClick(e, i)
            })
        })

        this.markActiveItem(0)
    },

    updateMarkerSize() {
        // Make sure the Alpine refs are initialized
        if (!this.$refs.marker || !this.$refs.radio1 || !this.$refs.radio2) return

        const activeRadio = this.selectedTab === 1 ? this.$refs.radio1 : this.$refs.radio2
        const activeLabel = activeRadio.nextElementSibling
        if (!activeLabel) return

        // Get the width of the active label including padding
        const labelWidth = activeLabel.offsetWidth
        const switchPadding = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue("--radio-switch-padding"),
        )

        // Calculate the position based on selected tab
        let markerLeft = switchPadding
        if (this.selectedTab === 2) {
            const firstLabel = this.$refs.radio1.nextElementSibling
            const firstLabelWidth = firstLabel.offsetWidth
            const gap = parseInt(getComputedStyle(document.querySelector(".radio-switch")).columnGap || "8px")
            markerLeft = firstLabelWidth + switchPadding * 2 + gap
        }

        // Apply the changes to the marker element directly
        this.$refs.marker.style.width = `${labelWidth}px`
        this.$refs.marker.style.transform =
            this.selectedTab === 1 ? "translateX(0)" : `translateX(${markerLeft - switchPadding}px)`
    },

    selectTab(tabNumber) {
        this.selectedTab = tabNumber
        this.$nextTick(() => {
            if (tabNumber === 1) {
                this.$refs.radio1.checked = true
            } else if (tabNumber === 2) {
                this.$refs.radio2.checked = true
                if (!this.isMobile) {
                    this.markActiveItem(0)
                }
            }

            // Update marker size when tab changes
            this.updateMarkerSize()

            // Refresh the slider for mobile view
            if (this.isMobile && this.sliders[tabNumber]) {
                this.sliders[tabNumber].refresh()
            }
        })
    },

    handleItemClick(e, index) {
        if (this.isMobile) return // Disable click handling on mobile

        let current = this.wheels[this.selectedTab].tracker.item
        let activeItem = index

        if (index === current) {
            return true
        }

        e.preventDefault()

        this.markActiveItem(activeItem)

        let diff = current - index

        if (Math.abs(diff) < this.wheels[this.selectedTab].numItems / 2) {
            this.moveWheel(diff * this.wheels[this.selectedTab].itemStep)
        } else {
            let amt = this.wheels[this.selectedTab].numItems - Math.abs(diff)

            if (current > index) {
                this.moveWheel(amt * -this.wheels[this.selectedTab].itemStep)
            } else {
                this.moveWheel(amt * this.wheels[this.selectedTab].itemStep)
            }
        }
    },

    up() {
        if (this.isMobile) {
            if (this.sliders[this.selectedTab]) {
                this.sliders[this.selectedTab].go("-1")
            }
        } else {
            this.moveWheel(this.wheels[this.selectedTab].itemStep)
        }
    },

    down() {
        if (this.isMobile) {
            if (this.sliders[this.selectedTab]) {
                this.sliders[this.selectedTab].go("+1")
            }
        } else {
            this.moveWheel(-this.wheels[this.selectedTab].itemStep)
        }
    },

    moveWheel(amount) {
        if (!this.wheels[this.selectedTab] || !this.wheels[this.selectedTab].tl) return

        let progress = this.wheels[this.selectedTab].tl.progress()
        this.wheels[this.selectedTab].tl.progress(
            this.wheels[this.selectedTab].wrapProgress(
                this.wheels[this.selectedTab].snap(this.wheels[this.selectedTab].tl.progress() + amount),
            ),
        )
        let next = this.wheels[this.selectedTab].tracker.item
        this.wheels[this.selectedTab].tl.progress(progress)

        this.markActiveItem(next)

        gsap.to(this.wheels[this.selectedTab].tl, {
            progress: this.wheels[this.selectedTab].snap(this.wheels[this.selectedTab].tl.progress() + amount),
            modifiers: {
                progress: this.wheels[this.selectedTab].wrapProgress,
            },
        })
    },

    markActiveItem(index) {
        if (!this.wheels[this.selectedTab] || !this.wheels[this.selectedTab].wrapper) return

        this.wheels[this.selectedTab].wrapper.querySelector(".item.active")?.classList.remove("active")
        this.wheels[this.selectedTab].items.forEach((el) => el.classList.remove("aside-active"))

        this.wheels[this.selectedTab].items[index].classList.add("active")

        const prevIndex = (index - 1 + this.wheels[this.selectedTab].numItems) % this.wheels[this.selectedTab].numItems
        const nextIndex = (index + 1) % this.wheels[this.selectedTab].numItems

        this.wheels[this.selectedTab].items[prevIndex].classList.add("aside-active")
        this.wheels[this.selectedTab].items[nextIndex].classList.add("aside-active")
    },
}))
